import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';
import { RootState } from '../../../store';
import {
  fetchCallDetail,
  fetchCalls,
} from '../../../store/callStats/actionCreators';
import {
  CallLogRow,
  CallStatus,
  CallType,
  scoreBand,
} from '../../../store/callStats/callStatsTypes';
import ChartPanel from '../components/ChartPanel';
import ScoreCard from '../components/ScoreCard';
import KpiCard from '../components/KpiCard';
import { bandColor } from '../components/scoreColors';
import {
  borderGrey,
  customError,
  customSuccess,
  lightGrey,
  warningYellow,
} from '../../../mainTheme';

const statusColor: Record<CallStatus, string> = {
  completed: customSuccess,
  no_answer: warningYellow,
  rejected: warningYellow,
  disconnected: customError,
  failed: customError,
  // in-flight calls — neutral; not offered as filter options (backend rejects them)
  ringing: lightGrey,
  in_progress: lightGrey,
};

const fmtDur = (sec: number) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const numOrDash = (v?: number | null, suffix = '', digits = 0) =>
  v === null || v === undefined ? '—' : `${v.toFixed(digits)}${suffix}`;

const ICE_LABEL: Record<string, string> = {
  host: 'Host',
  srflx_stun: 'STUN',
  relay_turn: 'TURN',
};

const TYPE_OPTS: { value: CallType | 'all'; label: string }[] = [
  { value: 'all', label: 'All types' },
  { value: 'video', label: 'Video' },
  { value: 'sip', label: 'SIP' },
];
const STATUS_OPTS: { value: CallStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'disconnected', label: 'Disconnected' },
  { value: 'failed', label: 'Failed' },
];

const CallLogSection: React.FC = () => {
  const dispatch = useDispatch();
  const { calls, callDetail, filters } = useSelector(
    (state: RootState) => state.callStats,
  );
  const [type, setType] = useState<CallType | 'all'>('all');
  const [status, setStatus] = useState<CallStatus | 'all'>('all');
  const [detailOpen, setDetailOpen] = useState(false);
  // Which call the dialog was opened for — the panel keeps the prior call's
  // data during a refetch/failure, so guard on this before rendering it.
  const [openCallId, setOpenCallId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(
      fetchCalls({
        ...filters,
        type: type === 'all' ? undefined : type,
        status: status === 'all' ? undefined : status,
        page: 0,
        pageSize: 250,
      }) as never,
    );
  }, [dispatch, filters, type, status]);

  const onRowClick = (params: GridRowParams) => {
    setOpenCallId(String(params.id));
    dispatch(fetchCallDetail(String(params.id)) as never);
    setDetailOpen(true);
  };

  const columns: GridColDef[] = [
    {
      field: 'startedAt',
      headerName: 'Started',
      flex: 1.4,
      minWidth: 150,
      valueFormatter: ({ value }) =>
        moment(value as string).format('MMM D, HH:mm:ss'),
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 0.7,
      minWidth: 80,
      renderCell: (p: GridRenderCellParams) => (
        <Chip
          label={String(p.value).toUpperCase()}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 120,
      renderCell: (p: GridRenderCellParams) => (
        <Chip
          label={String(p.value).replace('_', ' ')}
          size="small"
          sx={{
            color: '#fff',
            backgroundColor: statusColor[p.value as CallStatus],
          }}
        />
      ),
    },
    {
      field: 'durationSec',
      headerName: 'Duration',
      flex: 0.8,
      minWidth: 90,
      valueFormatter: ({ value }) => fmtDur(value as number),
    },
    {
      field: 'participants',
      headerName: 'Participants',
      flex: 1.8,
      minWidth: 180,
      valueGetter: ({ row }) =>
        (row as CallLogRow).participants.map((x) => x.displayName).join(' ↔ '),
    },
    {
      field: 'experienceScore',
      headerName: 'Exp.',
      flex: 0.7,
      minWidth: 80,
      renderCell: (p: GridRenderCellParams) =>
        p.value == null ? (
          '—'
        ) : (
          <Box
            sx={{
              color: bandColor[scoreBand(p.value as number)],
              fontWeight: 600,
            }}
          >
            {Math.round(p.value as number)}
          </Box>
        ),
    },
    {
      field: 'mos',
      headerName: 'MOS',
      flex: 0.6,
      minWidth: 70,
      valueFormatter: ({ value }) => numOrDash(value as number, '', 2),
    },
    {
      field: 'rttMs',
      headerName: 'RTT',
      flex: 0.6,
      minWidth: 80,
      valueFormatter: ({ value }) => numOrDash(value as number, ' ms'),
    },
    {
      field: 'packetLossPct',
      headerName: 'Loss',
      flex: 0.6,
      minWidth: 80,
      valueFormatter: ({ value }) => numOrDash(value as number, '%', 1),
    },
    {
      field: 'jitterMs',
      headerName: 'Jitter',
      flex: 0.6,
      minWidth: 80,
      valueFormatter: ({ value }) => numOrDash(value as number, ' ms', 1),
    },
    {
      field: 'bitrateKbps',
      headerName: 'Bitrate',
      flex: 0.7,
      minWidth: 90,
      valueFormatter: ({ value }) => numOrDash(value as number, ' kbps'),
    },
    {
      field: 'iceType',
      headerName: 'ICE',
      flex: 0.6,
      minWidth: 80,
      valueFormatter: ({ value }) => (value ? ICE_LABEL[value as string] : '—'),
    },
  ];

  const rows = (calls.data?.rows ?? []).map((r) => ({ id: r.callId, ...r }));
  // Only trust the panel's data when it's for the call the dialog is showing
  // (a stale/previous-call response must not render under a new id).
  const d =
    callDetail.data && callDetail.data.callId === openCallId
      ? callDetail.data
      : null;

  return (
    <ChartPanel
      title="Call log"
      subtitle="Per-call metrics — click a row for the detailed timeline"
      loading={calls.loading}
      error={calls.error}
      empty={!calls.loading && rows.length === 0}
      mocked={calls.mocked}
      minHeight={520}
      action={
        <Box display="flex" gap={1}>
          <TextField
            select
            size="small"
            value={type}
            onChange={(e) => setType(e.target.value as CallType | 'all')}
            sx={{ width: 130 }}
          >
            {TYPE_OPTS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            value={status}
            onChange={(e) => setStatus(e.target.value as CallStatus | 'all')}
            sx={{ width: 160 }}
          >
            {STATUS_OPTS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      }
    >
      <Box width="100%" height={500}>
        <DataGrid
          rows={rows}
          columns={columns}
          onRowClick={onRowClick}
          density="compact"
          disableRowSelectionOnClick
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
            sorting: { sortModel: [{ field: 'startedAt', sort: 'desc' }] },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          sx={{
            border: `1px solid ${borderGrey}`,
            borderRadius: '12px',
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F6F6F9' },
          }}
        />
      </Box>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Call detail
          {d && (
            <Typography
              variant="caption"
              sx={{ color: lightGrey, display: 'block' }}
            >
              {d.callId} · {moment(d.startedAt).format('MMM D, HH:mm:ss')} ·{' '}
              {d.type.toUpperCase()} · {fmtDur(d.durationSec)} ·{' '}
              {d.iceType ? ICE_LABEL[d.iceType] : '—'}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {callDetail.loading ? (
            <Box py={4} textAlign="center">
              <Typography variant="body2" sx={{ color: lightGrey }}>
                Loading…
              </Typography>
            </Box>
          ) : callDetail.error || !d ? (
            <Box py={4} textAlign="center">
              <Typography variant="body2" sx={{ color: lightGrey }}>
                {callDetail.error
                  ? 'No quality data captured for this call.'
                  : 'No detail available.'}
              </Typography>
            </Box>
          ) : (
            <Box>
              <Box display="flex" gap={1.5} flexWrap="wrap" mb={2}>
                <Box sx={{ flex: '1 1 130px', minWidth: 120 }}>
                  <ScoreCard
                    label="Experience"
                    score={d.scores.experience}
                    band={scoreBand(d.scores.experience)}
                    highlight
                  />
                </Box>
                <Box sx={{ flex: '1 1 130px', minWidth: 120 }}>
                  <KpiCard
                    label="MOS"
                    value={
                      d.metrics.mos != null ? d.metrics.mos.toFixed(2) : '—'
                    }
                  />
                </Box>
                <Box sx={{ flex: '1 1 130px', minWidth: 120 }}>
                  <KpiCard
                    label="RTT"
                    value={numOrDash(d.metrics.rttMs, ' ms', 0)}
                  />
                </Box>
                <Box sx={{ flex: '1 1 130px', minWidth: 120 }}>
                  <KpiCard
                    label="Packet loss"
                    value={numOrDash(d.metrics.packetLossPct, '%', 1)}
                  />
                </Box>
                <Box sx={{ flex: '1 1 130px', minWidth: 120 }}>
                  <KpiCard
                    label="Jitter"
                    value={numOrDash(d.metrics.jitterMs, ' ms', 1)}
                  />
                </Box>
              </Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Metric timeline
              </Typography>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart
                  data={d.timeseries}
                  margin={{ top: 8, right: 12, left: -12, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="t"
                    tickFormatter={(v) => moment(v).format('mm:ss')}
                    tick={{ fontSize: 11 }}
                    minTickGap={24}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip
                    labelFormatter={(v) =>
                      moment(v as string).format('HH:mm:ss')
                    }
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="rttMs"
                    name="RTT (ms)"
                    stroke={customError}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="jitterMs"
                    name="Jitter (ms)"
                    stroke={warningYellow}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="lossPct"
                    name="Loss (%)"
                    stroke="#865E9C"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </ChartPanel>
  );
};

export default CallLogSection;
